/**
 * Single source of truth for per-tool/per-conversion SEO.
 *
 * - title / description / keywords: head meta (single string, keyword-focused).
 * - h1 / intro / howTo / features / faq / related: VISIBLE on-page content,
 *   bilingual ({ en, id }) so it follows the global language toggle. Use
 *   localizeTool / localizeConvert to pick the active locale.
 *
 * Keep keywords aligned with docs/ShpAid_SEO_Master_Plan.txt §5.
 */

export type Locale = "en" | "id";
export interface L { en: string; id: string; }
const pick = (l: L, loc: Locale) => l[loc] ?? l.en;

export interface HowToStepL { name: L; text: L; }
export interface FaqItemL { q: L; a: L; }
export interface RelatedL { label: L; toolId: string; }

export interface ToolSeo {
  id: string;
  slug: string;
  title: string;
  description: string;
  primaryKeyword: string;
  keywords: string[];
  h1: L;
  intro: L;
  howTo: { name: L; steps: HowToStepL[] };
  features: L[];
  faq: FaqItemL[];
  related: RelatedL[];
}

/** Plain (already-localized) shape consumed by SeoArticle + schema builders. */
export interface LocalizedSeo {
  h1: string;
  intro: string;
  howTo: { name: string; steps: { name: string; text: string }[] };
  features: string[];
  faq: { q: string; a: string }[];
  related: { label: string; href: string }[];
}

const BASE = "https://www.shpaid.com";

export const TOOL_SEO: Record<string, ToolSeo> = {
  reproject: {
    id: "reproject",
    slug: "/tools/reproject",
    title: "Reproject Shapefile Online (EPSG, UTM, DGN95) | Shp.Aid",
    description:
      "Reproject shapefile online antar EPSG: WGS84, UTM 46-54 N/S, dan DGN95 TM-3. Deteksi otomatis dari .prj, tanpa unggah. Reproject shapefiles in your browser, free.",
    primaryKeyword: "reproject shapefile online",
    keywords: ["reproject shapefile online", "epsg converter", "utm converter", "shapefile crs converter", "dgn95 tm-3"],
    h1: { en: "Reproject Shapefile Online", id: "Reproyeksi Shapefile Online" },
    intro: {
      en: "Change the coordinate reference system of a shapefile between EPSG codes (WGS84, UTM, DGN95 TM-3) directly in your browser. No QGIS install, no upload, 100% free.",
      id: "Ubah sistem referensi koordinat (CRS) shapefile antar kode EPSG (WGS84, UTM, DGN95 TM-3) langsung di browser. Tanpa instal QGIS, tanpa unggah, 100% gratis.",
    },
    howTo: {
      name: { en: "How to reproject a shapefile online", id: "Cara reproyeksi shapefile online" },
      steps: [
        { name: { en: "Upload", id: "Unggah" }, text: { en: "Drop a zipped shapefile or the .shp/.dbf/.prj set. The source EPSG is detected from the .prj file.", id: "Seret shapefile (.zip atau set .shp/.dbf/.prj). EPSG sumber dideteksi dari file .prj." } },
        { name: { en: "Pick target CRS", id: "Pilih CRS target" }, text: { en: "Choose the target projection, for example UTM 48S (EPSG:32748) or WGS84 (EPSG:4326).", id: "Pilih proyeksi tujuan, misalnya UTM 48S (EPSG:32748) atau WGS84 (EPSG:4326)." } },
        { name: { en: "Download", id: "Unduh" }, text: { en: "Reproject and download the new shapefile, including a correct .prj. Everything runs locally in your browser.", id: "Reproyeksi dan unduh shapefile baru beserta .prj yang benar. Semua berjalan lokal di browser." } },
      ],
    },
    features: [
      { en: "No installation", id: "Tanpa instalasi" },
      { en: "Browser based", id: "Berbasis browser" },
      { en: "Private, no upload", id: "Privat, tanpa unggah" },
      { en: "WGS84 / UTM / DGN95 support", id: "Dukungan WGS84 / UTM / DGN95" },
    ],
    faq: [
      { q: { en: "Which projections are supported?", id: "Proyeksi apa saja yang didukung?" }, a: { en: "WGS84 (4326), Web Mercator (3857), UTM Indonesia 46N-54S, and DGN95 TM-3 zones, among others.", id: "WGS84 (4326), Web Mercator (3857), UTM Indonesia 46N-54S, dan zona DGN95 TM-3, di antaranya." } },
      { q: { en: "Do I need QGIS or ArcGIS?", id: "Perlu QGIS atau ArcGIS?" }, a: { en: "No. Reprojection runs in the browser with proj4, so no desktop GIS is required.", id: "Tidak. Reproyeksi berjalan di browser dengan proj4, jadi tidak perlu GIS desktop." } },
      { q: { en: "Is my file uploaded?", id: "Apakah file saya diunggah?" }, a: { en: "No. The shapefile is processed entirely on your device and never sent to a server.", id: "Tidak. Shapefile diproses sepenuhnya di perangkat Anda dan tidak pernah dikirim ke server." } },
    ],
    related: [
      { label: { en: "Coordinate converter", id: "Konverter koordinat" }, toolId: "coordinate" },
      { label: { en: "Format converter", id: "Konverter format" }, toolId: "converter" },
    ],
  },

  converter: {
    id: "converter",
    slug: "/tools/converter",
    title: "Shapefile Converter Online (SHP, GeoJSON, KML, CSV) | Shp.Aid",
    description:
      "Free online shapefile converter. Convert SHP to and from GeoJSON, KML, CSV, GPX, and DXF in your browser. No upload, no install. Konversi shapefile online gratis.",
    primaryKeyword: "shapefile converter online",
    keywords: ["shapefile converter online", "gis converter online", "shp to geojson", "shp to kml", "geojson to shp"],
    h1: { en: "Shapefile Converter Online", id: "Konverter Shapefile Online" },
    intro: {
      en: "Convert shapefiles to and from GeoJSON, KML, CSV, GPX, and DXF directly in your browser. A fast, private alternative to installing desktop GIS.",
      id: "Konversi shapefile ke dan dari GeoJSON, KML, CSV, GPX, dan DXF langsung di browser. Alternatif cepat dan privat tanpa instal GIS desktop.",
    },
    howTo: {
      name: { en: "How to convert a shapefile online", id: "Cara konversi shapefile online" },
      steps: [
        { name: { en: "Pick formats", id: "Pilih format" }, text: { en: "Choose the source format (SHP, GeoJSON, CSV) and the target format (GeoJSON, KML, CSV, GPX, DXF).", id: "Pilih format asal (SHP, GeoJSON, CSV) dan format tujuan (GeoJSON, KML, CSV, GPX, DXF)." } },
        { name: { en: "Upload", id: "Unggah" }, text: { en: "Drop your file. It is read locally in the browser.", id: "Seret file Anda. File dibaca lokal di browser." } },
        { name: { en: "Download", id: "Unduh" }, text: { en: "Convert and download the result. No data leaves your device.", id: "Konversi dan unduh hasilnya. Tidak ada data yang keluar dari perangkat." } },
      ],
    },
    features: [
      { en: "SHP, GeoJSON, KML, CSV, GPX, DXF", id: "SHP, GeoJSON, KML, CSV, GPX, DXF" },
      { en: "Browser based", id: "Berbasis browser" },
      { en: "Private, no upload", id: "Privat, tanpa unggah" },
      { en: "Free, no sign-up", id: "Gratis, tanpa daftar" },
    ],
    faq: [
      { q: { en: "Which formats are supported?", id: "Format apa saja yang didukung?" }, a: { en: "Shapefile, GeoJSON, KML, CSV, GPX, and DXF, in both directions where applicable.", id: "Shapefile, GeoJSON, KML, CSV, GPX, dan DXF, dua arah bila memungkinkan." } },
      { q: { en: "Can I convert SHP to KML for Google Earth?", id: "Bisa convert SHP ke KML untuk Google Earth?" }, a: { en: "Yes. Pick SHP as source and KML as target; the output is reprojected to WGS84 for Google Earth.", id: "Ya. Pilih SHP sebagai sumber dan KML sebagai tujuan; hasil diproyeksikan ke WGS84 untuk Google Earth." } },
      { q: { en: "Is it really free?", id: "Apakah benar-benar gratis?" }, a: { en: "Yes, with no account and no upload. Processing happens in your browser.", id: "Ya, tanpa akun dan tanpa unggah. Pemrosesan terjadi di browser Anda." } },
    ],
    related: [
      { label: { en: "Reproject", id: "Reproyeksi" }, toolId: "reproject" },
      { label: { en: "Validate & fix", id: "Validasi & perbaiki" }, toolId: "validate" },
    ],
  },

  validate: {
    id: "validate",
    slug: "/tools/validate",
    title: "Validate & Fix Shapefile Geometry Online | Shp.Aid",
    description:
      "Validate and repair invalid shapefile geometry online: self-intersections, unclosed rings, duplicate points. Runs in the browser, no upload. Validasi geometri shapefile.",
    primaryKeyword: "validate shapefile online",
    keywords: ["validate shapefile online", "fix shapefile geometry", "repair geometry gis", "self intersection fix"],
    h1: { en: "Validate and Fix Shapefile Geometry", id: "Validasi dan Perbaiki Geometri Shapefile" },
    intro: {
      en: "Detect and repair invalid geometry in shapefiles, such as self-intersections, unclosed rings, and duplicate vertices, without leaving your browser.",
      id: "Deteksi dan perbaiki geometri shapefile yang tidak valid, seperti self-intersection, ring tidak tertutup, dan verteks duplikat, tanpa keluar dari browser.",
    },
    howTo: {
      name: { en: "How to validate and fix a shapefile", id: "Cara validasi dan perbaiki shapefile" },
      steps: [
        { name: { en: "Upload", id: "Unggah" }, text: { en: "Drop a shapefile. The geometry is checked for common errors.", id: "Seret shapefile. Geometri diperiksa untuk kesalahan umum." } },
        { name: { en: "Review", id: "Tinjau" }, text: { en: "See which features have self-intersections, unclosed rings, or zero area.", id: "Lihat fitur mana yang punya self-intersection, ring tidak tertutup, atau luas nol." } },
        { name: { en: "Fix & download", id: "Perbaiki & unduh" }, text: { en: "Apply automatic fixes and download the cleaned shapefile.", id: "Terapkan perbaikan otomatis dan unduh shapefile yang sudah bersih." } },
      ],
    },
    features: [
      { en: "Self-intersection repair", id: "Perbaikan self-intersection" },
      { en: "Ring closing", id: "Penutupan ring" },
      { en: "Duplicate removal", id: "Hapus duplikat" },
      { en: "Private, no upload", id: "Privat, tanpa unggah" },
    ],
    faq: [
      { q: { en: "What errors are detected?", id: "Kesalahan apa yang dideteksi?" }, a: { en: "Self-intersections, unclosed rings, zero-area polygons, duplicate points, and invalid coordinates.", id: "Self-intersection, ring tidak tertutup, poligon luas nol, titik duplikat, dan koordinat tidak valid." } },
      { q: { en: "How are geometries fixed?", id: "Bagaimana geometri diperbaiki?" }, a: { en: "Using Turf operations such as cleanCoords, buffer(0), and rewind to restore valid topology.", id: "Memakai operasi Turf seperti cleanCoords, buffer(0), dan rewind untuk memulihkan topologi yang valid." } },
      { q: { en: "Is my data private?", id: "Apakah data saya privat?" }, a: { en: "Yes. Validation runs entirely in the browser.", id: "Ya. Validasi berjalan sepenuhnya di browser." } },
    ],
    related: [
      { label: { en: "Simplify", id: "Sederhanakan" }, toolId: "simplify" },
      { label: { en: "Merge", id: "Gabung" }, toolId: "merge" },
    ],
  },

  buffer: {
    id: "buffer",
    slug: "/tools/buffer",
    title: "Buffer Shapefile Online (Points, Lines, Polygons) | Shp.Aid",
    description:
      "Create buffer zones around shapefile features online. Set distance and units for points, lines, and polygons. No upload, free. Buffer shapefile online di browser.",
    primaryKeyword: "buffer shapefile online",
    keywords: ["buffer shapefile online", "gis buffer tool", "buffer polygon online", "shapefile buffer"],
    h1: { en: "Buffer Shapefile Online", id: "Buffer Shapefile Online" },
    intro: {
      en: "Generate buffer zones around points, lines, and polygons in a shapefile with a chosen distance and unit, directly in your browser.",
      id: "Buat zona buffer di sekitar titik, garis, dan poligon pada shapefile dengan jarak dan satuan pilihan, langsung di browser.",
    },
    howTo: {
      name: { en: "How to buffer a shapefile online", id: "Cara buffer shapefile online" },
      steps: [
        { name: { en: "Upload", id: "Unggah" }, text: { en: "Drop a shapefile containing points, lines, or polygons.", id: "Seret shapefile berisi titik, garis, atau poligon." } },
        { name: { en: "Set distance", id: "Atur jarak" }, text: { en: "Enter the buffer distance and unit (meters or degrees).", id: "Masukkan jarak buffer dan satuannya (meter atau derajat)." } },
        { name: { en: "Download", id: "Unduh" }, text: { en: "Generate the buffer and download the result shapefile.", id: "Hasilkan buffer dan unduh shapefile hasilnya." } },
      ],
    },
    features: [
      { en: "Points, lines, polygons", id: "Titik, garis, poligon" },
      { en: "Meter or degree units", id: "Satuan meter atau derajat" },
      { en: "Browser based", id: "Berbasis browser" },
      { en: "Private, no upload", id: "Privat, tanpa unggah" },
    ],
    faq: [
      { q: { en: "What units can I use?", id: "Satuan apa yang bisa dipakai?" }, a: { en: "Meters or degrees, depending on your data's coordinate system.", id: "Meter atau derajat, tergantung sistem koordinat data Anda." } },
      { q: { en: "Can I buffer lines and points?", id: "Bisa buffer garis dan titik?" }, a: { en: "Yes. Buffering works for point, line, and polygon geometries.", id: "Ya. Buffer bekerja untuk geometri titik, garis, dan poligon." } },
      { q: { en: "Is upload required?", id: "Perlu unggah?" }, a: { en: "No. The buffer is computed in your browser.", id: "Tidak. Buffer dihitung di browser Anda." } },
    ],
    related: [
      { label: { en: "Clip", id: "Potong" }, toolId: "clip" },
      { label: { en: "Measurements", id: "Pengukuran" }, toolId: "calculate" },
    ],
  },

  simplify: {
    id: "simplify",
    slug: "/tools/simplify",
    title: "Simplify Shapefile Geometry Online (Douglas-Peucker) | Shp.Aid",
    description:
      "Simplify shapefile geometry online with Douglas-Peucker. Reduce vertices while keeping shape, no upload, free. Sederhanakan geometri shapefile di browser.",
    primaryKeyword: "simplify shapefile online",
    keywords: ["simplify shapefile online", "reduce vertices shapefile", "douglas peucker online", "generalize geometry"],
    h1: { en: "Simplify Shapefile Geometry", id: "Sederhanakan Geometri Shapefile" },
    intro: {
      en: "Reduce the number of vertices in a shapefile using the Douglas-Peucker algorithm while preserving the essential shape, all in your browser.",
      id: "Kurangi jumlah verteks shapefile dengan algoritma Douglas-Peucker tanpa kehilangan bentuk penting, semuanya di browser.",
    },
    howTo: {
      name: { en: "How to simplify a shapefile online", id: "Cara sederhanakan shapefile online" },
      steps: [
        { name: { en: "Upload", id: "Unggah" }, text: { en: "Drop a shapefile with lines or polygons.", id: "Seret shapefile berisi garis atau poligon." } },
        { name: { en: "Set tolerance", id: "Atur toleransi" }, text: { en: "Choose a simplification tolerance, or use the recommended value for your extent.", id: "Pilih toleransi penyederhanaan, atau pakai nilai rekomendasi sesuai cakupan data." } },
        { name: { en: "Download", id: "Unduh" }, text: { en: "Simplify and download the lighter shapefile.", id: "Sederhanakan dan unduh shapefile yang lebih ringan." } },
      ],
    },
    features: [
      { en: "Douglas-Peucker", id: "Douglas-Peucker" },
      { en: "Tolerance recommendation", id: "Rekomendasi toleransi" },
      { en: "Browser based", id: "Berbasis browser" },
      { en: "Private, no upload", id: "Privat, tanpa unggah" },
    ],
    faq: [
      { q: { en: "What algorithm is used?", id: "Algoritma apa yang dipakai?" }, a: { en: "Douglas-Peucker, which removes vertices that fall within the tolerance distance.", id: "Douglas-Peucker, yang menghapus verteks yang berada dalam jarak toleransi." } },
      { q: { en: "Will it distort my data?", id: "Apakah merusak data?" }, a: { en: "Higher tolerance removes more detail. A recommended value balances size and accuracy.", id: "Toleransi lebih tinggi menghapus lebih banyak detail. Nilai rekomendasi menyeimbangkan ukuran dan akurasi." } },
      { q: { en: "Is my file uploaded?", id: "Apakah file saya diunggah?" }, a: { en: "No. Simplification runs in the browser.", id: "Tidak. Penyederhanaan berjalan di browser." } },
    ],
    related: [
      { label: { en: "Validate & fix", id: "Validasi & perbaiki" }, toolId: "validate" },
      { label: { en: "Reproject", id: "Reproyeksi" }, toolId: "reproject" },
    ],
  },

  calculate: {
    id: "calculate",
    slug: "/tools/calculate",
    title: "Measure Area, Length & Perimeter of Shapefile | Shp.Aid",
    description:
      "Measure shapefile area, length, perimeter, centroid, and bounds online, then export to CSV. No upload, free. Ukur luas dan panjang shapefile di browser.",
    primaryKeyword: "measure shapefile area online",
    keywords: ["measure shapefile area", "shapefile area calculator", "polygon area online", "length perimeter gis"],
    h1: { en: "Measure Shapefile Area and Length", id: "Ukur Luas dan Panjang Shapefile" },
    intro: {
      en: "Compute area, length, perimeter, centroid, and bounding box for shapefile features and export the results to CSV, directly in your browser.",
      id: "Hitung luas, panjang, keliling, centroid, dan bounding box fitur shapefile lalu ekspor hasilnya ke CSV, langsung di browser.",
    },
    howTo: {
      name: { en: "How to measure a shapefile online", id: "Cara mengukur shapefile online" },
      steps: [
        { name: { en: "Upload", id: "Unggah" }, text: { en: "Drop a shapefile with the features to measure.", id: "Seret shapefile berisi fitur yang akan diukur." } },
        { name: { en: "Choose metrics", id: "Pilih metrik" }, text: { en: "Select units and the metrics you need: area, length, perimeter, centroid, or bounds.", id: "Pilih satuan dan metrik yang dibutuhkan: luas, panjang, keliling, centroid, atau bounds." } },
        { name: { en: "Export", id: "Ekspor" }, text: { en: "Write results to the attributes or export them as CSV.", id: "Tulis hasil ke atribut atau ekspor sebagai CSV." } },
      ],
    },
    features: [
      { en: "Area, length, perimeter", id: "Luas, panjang, keliling" },
      { en: "Centroid & bounds", id: "Centroid & bounds" },
      { en: "CSV export", id: "Ekspor CSV" },
      { en: "Private, no upload", id: "Privat, tanpa unggah" },
    ],
    faq: [
      { q: { en: "Which metrics are available?", id: "Metrik apa yang tersedia?" }, a: { en: "Area, length, perimeter, centroid, and bounding box, per feature.", id: "Luas, panjang, keliling, centroid, dan bounding box, per fitur." } },
      { q: { en: "Can I export the results?", id: "Bisa ekspor hasilnya?" }, a: { en: "Yes, as CSV or written into the feature attributes.", id: "Ya, sebagai CSV atau ditulis ke atribut fitur." } },
      { q: { en: "Is processing local?", id: "Apakah proses lokal?" }, a: { en: "Yes. Measurements are computed in your browser.", id: "Ya. Pengukuran dihitung di browser Anda." } },
    ],
    related: [
      { label: { en: "Buffer", id: "Buffer" }, toolId: "buffer" },
      { label: { en: "Coordinate converter", id: "Konverter koordinat" }, toolId: "coordinate" },
    ],
  },

  merge: {
    id: "merge",
    slug: "/tools/merge",
    title: "Merge Shapefiles Online (Combine into One) | Shp.Aid",
    description:
      "Merge multiple shapefiles into one online while keeping geometry and CRS compatibility. No upload, free. Gabung beberapa shapefile menjadi satu di browser.",
    primaryKeyword: "merge shapefile online",
    keywords: ["merge shapefile online", "combine shapefiles", "join shapefiles online", "shapefile merge tool"],
    h1: { en: "Merge Shapefiles Online", id: "Gabung Shapefile Online" },
    intro: {
      en: "Combine several shapefiles into a single file while checking geometry and coordinate system compatibility, all in your browser.",
      id: "Gabungkan beberapa shapefile menjadi satu file sambil memeriksa kompatibilitas geometri dan sistem koordinat, semuanya di browser.",
    },
    howTo: {
      name: { en: "How to merge shapefiles online", id: "Cara gabung shapefile online" },
      steps: [
        { name: { en: "Upload", id: "Unggah" }, text: { en: "Drop two or more shapefiles to combine.", id: "Seret dua atau lebih shapefile untuk digabung." } },
        { name: { en: "Check compatibility", id: "Cek kompatibilitas" }, text: { en: "Geometry type and CRS are checked so the merge is valid.", id: "Tipe geometri dan CRS diperiksa agar penggabungan valid." } },
        { name: { en: "Download", id: "Unduh" }, text: { en: "Merge and download a single shapefile, with a source tag per feature.", id: "Gabung dan unduh satu shapefile, dengan tag sumber per fitur." } },
      ],
    },
    features: [
      { en: "Combine many files", id: "Gabung banyak file" },
      { en: "Geometry & CRS checks", id: "Cek geometri & CRS" },
      { en: "Source tagging", id: "Penandaan sumber" },
      { en: "Private, no upload", id: "Privat, tanpa unggah" },
    ],
    faq: [
      { q: { en: "Must files share a geometry type?", id: "Harus tipe geometri sama?" }, a: { en: "Yes, merged features should share a compatible geometry type and CRS.", id: "Ya, fitur yang digabung sebaiknya berbagi tipe geometri dan CRS yang kompatibel." } },
      { q: { en: "Can I tell which file a feature came from?", id: "Bisa tahu fitur dari file mana?" }, a: { en: "Yes, each feature is tagged with its source file name.", id: "Ya, setiap fitur ditandai dengan nama file asalnya." } },
      { q: { en: "Is upload required?", id: "Perlu unggah?" }, a: { en: "No. Merging happens in your browser.", id: "Tidak. Penggabungan terjadi di browser Anda." } },
    ],
    related: [
      { label: { en: "Validate & fix", id: "Validasi & perbaiki" }, toolId: "validate" },
      { label: { en: "Clip", id: "Potong" }, toolId: "clip" },
    ],
  },

  clip: {
    id: "clip",
    slug: "/tools/clip",
    title: "Clip Shapefile Online (by BBox or Polygon Mask) | Shp.Aid",
    description:
      "Clip a shapefile online by a bounding box or a second polygon shapefile mask. No upload, free. Potong shapefile berdasarkan bbox atau mask poligon di browser.",
    primaryKeyword: "clip shapefile online",
    keywords: ["clip shapefile online", "shapefile clip tool", "clip by polygon", "crop shapefile online"],
    h1: { en: "Clip Shapefile Online", id: "Potong Shapefile Online" },
    intro: {
      en: "Cut a shapefile to a bounding box or to a second polygon shapefile used as a mask, with pre-validation, directly in your browser.",
      id: "Potong shapefile berdasarkan bounding box atau shapefile poligon kedua sebagai mask, dengan pra-validasi, langsung di browser.",
    },
    howTo: {
      name: { en: "How to clip a shapefile online", id: "Cara potong shapefile online" },
      steps: [
        { name: { en: "Upload", id: "Unggah" }, text: { en: "Drop the shapefile you want to clip.", id: "Seret shapefile yang ingin dipotong." } },
        { name: { en: "Choose mask", id: "Pilih mask" }, text: { en: "Clip by a bounding box, the current extent, or a second polygon shapefile as a mask.", id: "Potong dengan bounding box, extent saat ini, atau shapefile poligon kedua sebagai mask." } },
        { name: { en: "Download", id: "Unduh" }, text: { en: "Clip and download the result, with the original projection preserved.", id: "Potong dan unduh hasilnya, dengan proyeksi asli tetap dipertahankan." } },
      ],
    },
    features: [
      { en: "BBox or polygon mask", id: "Mask BBox atau poligon" },
      { en: "Extent pre-validation", id: "Pra-validasi extent" },
      { en: "Projection preserved", id: "Proyeksi dipertahankan" },
      { en: "Private, no upload", id: "Privat, tanpa unggah" },
    ],
    faq: [
      { q: { en: "Can I clip with another shapefile?", id: "Bisa potong dengan shapefile lain?" }, a: { en: "Yes. Use a second polygon shapefile as the clipping mask.", id: "Ya. Gunakan shapefile poligon kedua sebagai mask pemotong." } },
      { q: { en: "Is the projection kept?", id: "Apakah proyeksi dipertahankan?" }, a: { en: "Yes, the output keeps the source projection (.prj).", id: "Ya, output mempertahankan proyeksi sumber (.prj)." } },
      { q: { en: "Does it upload my data?", id: "Apakah mengunggah data saya?" }, a: { en: "No. Clipping runs in your browser.", id: "Tidak. Pemotongan berjalan di browser Anda." } },
    ],
    related: [
      { label: { en: "Buffer", id: "Buffer" }, toolId: "buffer" },
      { label: { en: "Merge", id: "Gabung" }, toolId: "merge" },
    ],
  },

  coordinate: {
    id: "coordinate",
    slug: "/tools/coordinate",
    title: "Coordinate Converter Online (DD, DMS, UTM, WGS84) | Shp.Aid",
    description:
      "Convert coordinates between Decimal Degrees (DD), DMS, and UTM online with proj4 precision, single or batch, CSV export. Konversi koordinat DD, DMS, UTM di browser.",
    primaryKeyword: "coordinate converter",
    keywords: ["coordinate converter", "utm to wgs84 converter", "dms to decimal degrees", "epsg transformation", "utm converter indonesia"],
    h1: { en: "Coordinate Converter (DD, DMS, UTM)", id: "Konverter Koordinat (DD, DMS, UTM)" },
    intro: {
      en: "Convert coordinates between Decimal Degrees, DMS, and UTM on the WGS84 datum with proj4 precision, for single points or batches, and view them on a map.",
      id: "Konversi koordinat antara Decimal Degrees, DMS, dan UTM pada datum WGS84 dengan presisi proj4, untuk titik tunggal atau batch, dan lihat di peta.",
    },
    howTo: {
      name: { en: "How to convert coordinates online", id: "Cara konversi koordinat online" },
      steps: [
        { name: { en: "Enter a coordinate", id: "Masukkan koordinat" }, text: { en: "Type a value in any section: Decimal Degrees, DMS, or UTM.", id: "Ketik nilai di bagian mana pun: Decimal Degrees, DMS, atau UTM." } },
        { name: { en: "Convert", id: "Konversi" }, text: { en: "All other formats are filled instantly using proj4.", id: "Semua format lain terisi otomatis memakai proj4." } },
        { name: { en: "View or export", id: "Lihat atau ekspor" }, text: { en: "Copy the result, view it on the map, or export a batch to CSV.", id: "Salin hasil, lihat di peta, atau ekspor batch ke CSV." } },
      ],
    },
    features: [
      { en: "DD, DMS, UTM", id: "DD, DMS, UTM" },
      { en: "WGS84 datum", id: "Datum WGS84" },
      { en: "Indonesia UTM zones", id: "Zona UTM Indonesia" },
      { en: "View on map", id: "Lihat di peta" },
    ],
    faq: [
      { q: { en: "Which datum is used?", id: "Datum apa yang dipakai?" }, a: { en: "WGS84 (EPSG:4326). UTM zones are derived automatically.", id: "WGS84 (EPSG:4326). Zona UTM dihitung otomatis." } },
      { q: { en: "Can I convert UTM to lat/long?", id: "Bisa convert UTM ke lat/long?" }, a: { en: "Yes, enter the UTM zone, easting, and northing to get Decimal Degrees and DMS.", id: "Ya, masukkan zona UTM, easting, dan northing untuk mendapat Decimal Degrees dan DMS." } },
      { q: { en: "Can I see the point on a map?", id: "Bisa lihat titik di peta?" }, a: { en: "Yes. Use the View on map button to preview the coordinate.", id: "Ya. Pakai tombol Lihat di peta untuk pratinjau koordinat." } },
    ],
    related: [
      { label: { en: "Reproject", id: "Reproyeksi" }, toolId: "reproject" },
      { label: { en: "Measurements", id: "Pengukuran" }, toolId: "calculate" },
    ],
  },

  layout: {
    id: "layout",
    slug: "/tools/layout",
    title: "Layout Peta & Print Cartographic Map Online | Shp.Aid",
    description:
      "Atur layout peta kartografi online dengan bingkai kertas A4/A3, kompas arah mata angin (North Arrow), skala garis, dan legenda terformat rapi di browser.",
    primaryKeyword: "layout peta online",
    keywords: ["layout peta online", "print map layout", "kartografi peta", "north arrow scalebar", "a4 map layout"],
    h1: { en: "Cartographic Map Layout & Print", id: "Layout & Cetak Peta Kartografi" },
    intro: {
      en: "Design cartographic map layouts with paper frames (A4/A3), North Arrow, scale bar, and automated legends directly in your browser.",
      id: "Rancang layout peta kartografi dengan bingkai kertas (A4/A3), Arah Mata Angin, skala garis, dan legenda terformat otomatis langsung di browser.",
    },
    howTo: {
      name: { en: "How to design a map layout online", id: "Cara membuat layout peta online" },
      steps: [
        { name: { en: "Upload data", id: "Unggah data" }, text: { en: "Upload shapefile or open Custom Canvas Studio.", id: "Unggah shapefile atau buka Studio Canvas Kartografi." } },
        { name: { en: "Configure elements", id: "Atur elemen" }, text: { en: "Title, North Arrow, scale bar, and legend render automatically.", id: "Judul, Arah Mata Angin, skala garis, dan legenda ter-render otomatis." } },
        { name: { en: "Print or Export", id: "Cetak atau Ekspor" }, text: { en: "Export PDF map layout or print directly.", id: "Ekspor layout peta PDF atau cetak langsung." } },
      ],
    },
    features: [
      { en: "Cartographic Paper Layout", id: "Layout Kertas Kartografi" },
      { en: "Automatic Scale Bar & Compass", id: "Skala Garis & Kompas Otomatis" },
      { en: "PDF Export & Printing", id: "Ekspor PDF & Cetak Peta" },
    ],
    faq: [
      { q: { en: "What paper sizes are supported?", id: "Ukuran kertas apa saja yang didukung?" }, a: { en: "A4, A3, and Letter size layouts.", id: "Layout ukuran A4, A3, dan Letter." } },
    ],
    related: [
      { label: { en: "Share Tool", id: "Bagikan Peta" }, toolId: "share" },
      { label: { en: "Reproject", id: "Reproyeksi" }, toolId: "reproject" },
    ],
  },

  share: {
    id: "share",
    slug: "/tools/share",
    title: "Share & Bagikan Peta GIS Online (End-to-End Encrypted) | Shp.Aid",
    description:
      "Bagikan data shapefile dan GeoJSON dengan tautan terenkripsi AES-256 langsung dari browser. Kontrol hak akses View, Edit, atau Manage tanpa backend.",
    primaryKeyword: "share shapefile online",
    keywords: ["share shapefile online", "bagikan peta gis", "encrypted gis share", "web map sharing", "shapefile viewer link"],
    h1: { en: "Share & Distribute GIS Data Encrypted", id: "Bagikan Peta & Data GIS Terenkripsi" },
    intro: {
      en: "Share shapefile and GeoJSON layers securely via end-to-end encrypted links (AES-256) directly in your browser. Set view, edit, or manage permissions.",
      id: "Bagikan layer shapefile dan GeoJSON secara aman melalui tautan terenkripsi end-to-end (AES-256) langsung di browser. Atur hak akses view, edit, atau manage.",
    },
    howTo: {
      name: { en: "How to share GIS data online", id: "Cara membagikan data GIS online" },
      steps: [
        { name: { en: "Select dataset", id: "Pilih dataset" }, text: { en: "Upload shapefile or use the current active map preview.", id: "Unggah shapefile atau gunakan pratinjau peta yang aktif." } },
        { name: { en: "Set permissions", id: "Atur hak akses" }, text: { en: "Choose View, Edit, or Manage mode and optional password.", id: "Pilih mode View, Edit, atau Manage serta sandi opsional." } },
        { name: { en: "Copy link", id: "Salin tautan" }, text: { en: "Copy the 24-hour end-to-end encrypted link and share with your team.", id: "Salin tautan terenkripsi 24 jam dan bagikan ke tim Anda." } },
      ],
    },
    features: [
      { en: "End-to-End AES-256 Encryption", id: "Enkripsi AES-256 End-to-End" },
      { en: "Client-side processing", id: "Pemrosesan di browser" },
      { en: "Access mode controls", id: "Kontrol mode akses" },
      { en: "24-hour self-expiring link", id: "Tautan kedaluwarsa 24 jam" },
    ],
    faq: [
      { q: { en: "Is my data stored securely?", id: "Apakah data disimpan secara aman?" }, a: { en: "Yes. Data is encrypted in your browser using AES-256 before upload. Only users with the link key can decrypt it.", id: "Ya. Data dienkripsi di browser menggunakan AES-256 sebelum diunggah. Hanya penerima dengan kunci tautan yang dapat mendekripsinya." } },
      { q: { en: "How long is the share link valid?", id: "Berapa lama tautan berlaku?" }, a: { en: "Share links automatically expire after 24 hours.", id: "Tautan berbagi otomatis kedaluwarsa setelah 24 jam." } },
    ],
    related: [
      { label: { en: "Reproject", id: "Reproyeksi" }, toolId: "reproject" },
      { label: { en: "Format converter", id: "Konverter format" }, toolId: "converter" },
    ],
  },
};

// Positioning: the free tools people find elsewhere only convert formats —
// buffer, clip, simplify and geometry validation are what set this apart, so
// they lead the title instead of being buried at the end of a feature list.
export const DEFAULT_TITLE =
  "Editor Shapefile Online Gratis — Buffer, Clip, Reproject | Shp.Aid";
export const DEFAULT_DESC =
  "Bukan sekadar konverter: buffer, clip, reproject, validasi geometri & simplify shapefile langsung di browser. Tanpa unggah, mendukung DGN95 dan UTM 48S/49S. Gratis.";

export function getToolSeo(id: string | null | undefined): ToolSeo | null {
  return id ? TOOL_SEO[id] ?? null : null;
}

export function localizeTool(seo: ToolSeo, loc: Locale): LocalizedSeo {
  return {
    h1: pick(seo.h1, loc),
    intro: pick(seo.intro, loc),
    howTo: {
      name: pick(seo.howTo.name, loc),
      steps: seo.howTo.steps.map((s) => ({ name: pick(s.name, loc), text: pick(s.text, loc) })),
    },
    features: seo.features.map((f) => pick(f, loc)),
    faq: seo.faq.map((f) => ({ q: pick(f.q, loc), a: pick(f.a, loc) })),
    related: seo.related.map((r) => ({ label: pick(r.label, loc), href: `/tools/${r.toolId}` })),
  };
}

/** Build JSON-LD @graph for a tool page from already-localized content. */
export function buildToolSchema(c: LocalizedSeo, slug: string) {
  const url = `${BASE}${slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: c.h1,
        url,
        applicationCategory: "GeographicInformationSystemApplication",
        operatingSystem: "Any (Browser)",
        isAccessibleForFree: true,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        description: c.intro,
      },
      {
        "@type": "HowTo",
        name: c.howTo.name,
        step: c.howTo.steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.name, text: s.text })),
      },
      {
        "@type": "FAQPage",
        mainEntity: c.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Shp.Aid", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: c.h1, item: url },
        ],
      },
    ],
  };
}

// ─── Conversion landing pages (/convert/<pair>) ─────────────────────────────────

export type InputFormat = "shp" | "geojson" | "csv";
export type OutputFormat = "shp" | "geojson" | "kml" | "csv" | "gpx" | "dxf";

export interface ConvertSeo {
  pair: string;
  slug: string;
  input: InputFormat;
  output: OutputFormat;
  title: string;
  description: string;
  primaryKeyword: string;
  keywords: string[];
  h1: L;
  intro: L;
  faq: FaqItemL[];
  related: { label: L; href: string }[];
}

const conv = (
  pair: string, input: InputFormat, output: OutputFormat,
  title: string, description: string, h1: L, intro: L,
  primaryKeyword: string, keywords: string[], faq: FaqItemL[],
  related: { label: L; href: string }[],
): ConvertSeo => ({ pair, slug: `/convert/${pair}`, input, output, title, description, primaryKeyword, keywords, h1, intro, faq, related });

const IN = (f: InputFormat | OutputFormat) => f.toUpperCase();

export const CONVERT_PAGES: Record<string, ConvertSeo> = {
  "shp-to-kml": conv(
    "shp-to-kml", "shp", "kml",
    "Convert SHP to KML Online (Free, No Upload) | Shp.Aid",
    "Convert SHP to KML online for Google Earth. Free browser-based shapefile to KML converter, no upload, no install. Konversi SHP ke KML online gratis.",
    { en: "Convert SHP to KML Online", id: "Convert SHP ke KML Online" },
    { en: "Convert a shapefile (SHP) to KML for Google Earth directly in your browser. The output is reprojected to WGS84, with no upload and no software to install.", id: "Convert shapefile (SHP) ke KML untuk Google Earth langsung di browser. Hasil diproyeksikan ke WGS84, tanpa unggah dan tanpa instal software." },
    "shp to kml",
    ["shp to kml", "shapefile to kml", "convert shapefile to google earth", "kml converter"],
    [
      { q: { en: "What is a KML file?", id: "Apa itu file KML?" }, a: { en: "KML is the format used by Google Earth and Google Maps to display geographic data.", id: "KML adalah format yang dipakai Google Earth dan Google Maps untuk menampilkan data geografis." } },
      { q: { en: "Is the output ready for Google Earth?", id: "Apakah hasil siap untuk Google Earth?" }, a: { en: "Yes. SHP is converted and reprojected to WGS84 so it opens correctly in Google Earth.", id: "Ya. SHP dikonversi dan diproyeksikan ke WGS84 agar terbuka benar di Google Earth." } },
      { q: { en: "Is my shapefile uploaded?", id: "Apakah shapefile saya diunggah?" }, a: { en: "No. The conversion runs entirely in your browser.", id: "Tidak. Konversi berjalan sepenuhnya di browser Anda." } },
    ],
    [{ label: { en: "SHP to GeoJSON", id: "SHP ke GeoJSON" }, href: "/convert/shp-to-geojson" }, { label: { en: "Reproject", id: "Reproyeksi" }, href: "/tools/reproject" }],
  ),
  "shp-to-geojson": conv(
    "shp-to-geojson", "shp", "geojson",
    "Convert SHP to GeoJSON Online (Free) | Shp.Aid",
    "Convert SHP to GeoJSON online in your browser. Free shapefile to GeoJSON converter, no upload required. Konversi SHP ke GeoJSON online gratis.",
    { en: "Convert SHP to GeoJSON Online", id: "Convert SHP ke GeoJSON Online" },
    { en: "Convert a shapefile (SHP) to GeoJSON, the standard format for the web, directly in your browser without uploading your data.", id: "Convert shapefile (SHP) ke GeoJSON, format standar untuk web, langsung di browser tanpa mengunggah data Anda." },
    "shp to geojson",
    ["shp to geojson", "shapefile to geojson", "convert shp to geojson online"],
    [
      { q: { en: "Why convert SHP to GeoJSON?", id: "Kenapa convert SHP ke GeoJSON?" }, a: { en: "GeoJSON is text-based and works natively with web maps and JavaScript libraries.", id: "GeoJSON berbasis teks dan langsung bekerja dengan peta web serta pustaka JavaScript." } },
      { q: { en: "Does it keep attributes?", id: "Apakah atribut dipertahankan?" }, a: { en: "Yes. Feature attributes from the .dbf are preserved in the GeoJSON properties.", id: "Ya. Atribut fitur dari .dbf dipertahankan sebagai properties GeoJSON." } },
      { q: { en: "Is it private?", id: "Apakah privat?" }, a: { en: "Yes. Conversion happens in your browser, with no upload.", id: "Ya. Konversi terjadi di browser, tanpa unggah." } },
    ],
    [{ label: { en: "GeoJSON to SHP", id: "GeoJSON ke SHP" }, href: "/convert/geojson-to-shp" }, { label: { en: "SHP to KML", id: "SHP ke KML" }, href: "/convert/shp-to-kml" }],
  ),
  "geojson-to-shp": conv(
    "geojson-to-shp", "geojson", "shp",
    "Convert GeoJSON to SHP Online (Free) | Shp.Aid",
    "Convert GeoJSON to shapefile (SHP) online in your browser. Free GeoJSON to SHP converter, no upload. Konversi GeoJSON ke SHP online gratis.",
    { en: "Convert GeoJSON to SHP Online", id: "Convert GeoJSON ke SHP Online" },
    { en: "Convert GeoJSON to an Esri shapefile (SHP) directly in your browser, with a correct .prj, and no upload.", id: "Convert GeoJSON ke shapefile Esri (SHP) langsung di browser, dengan .prj yang benar, dan tanpa unggah." },
    "geojson to shp",
    ["geojson to shp", "geojson to shapefile", "convert geojson to shp online"],
    [
      { q: { en: "What do I get?", id: "Apa yang saya dapat?" }, a: { en: "A zipped shapefile (.shp/.shx/.dbf/.prj) ready for desktop GIS.", id: "Shapefile dalam .zip (.shp/.shx/.dbf/.prj) siap untuk GIS desktop." } },
      { q: { en: "Which projection is written?", id: "Proyeksi apa yang ditulis?" }, a: { en: "WGS84 by default; you can pick another EPSG before downloading.", id: "WGS84 secara default; Anda bisa memilih EPSG lain sebelum mengunduh." } },
      { q: { en: "Is my file uploaded?", id: "Apakah file saya diunggah?" }, a: { en: "No. Everything runs in your browser.", id: "Tidak. Semua berjalan di browser Anda." } },
    ],
    [{ label: { en: "SHP to GeoJSON", id: "SHP ke GeoJSON" }, href: "/convert/shp-to-geojson" }, { label: { en: "Validate & fix", id: "Validasi & perbaiki" }, href: "/tools/validate" }],
  ),
  "shp-to-csv": conv(
    "shp-to-csv", "shp", "csv",
    "Convert SHP to CSV Online (Free) | Shp.Aid",
    "Convert shapefile to CSV online in your browser. Export attributes and coordinates to CSV, no upload. Konversi SHP ke CSV online gratis.",
    { en: "Convert SHP to CSV Online", id: "Convert SHP ke CSV Online" },
    { en: "Export a shapefile's attributes and coordinates to CSV directly in your browser, without uploading your data.", id: "Ekspor atribut dan koordinat shapefile ke CSV langsung di browser, tanpa mengunggah data Anda." },
    "shapefile to csv",
    ["shapefile to csv", "shp to csv", "convert shapefile to csv online"],
    [
      { q: { en: "What is in the CSV?", id: "Apa isi CSV-nya?" }, a: { en: "Feature attributes plus the first coordinate (longitude/latitude in WGS84).", id: "Atribut fitur plus koordinat pertama (bujur/lintang dalam WGS84)." } },
      { q: { en: "Can I open it in Excel?", id: "Bisa dibuka di Excel?" }, a: { en: "Yes. The CSV opens in Excel, Google Sheets, or any spreadsheet tool.", id: "Ya. CSV terbuka di Excel, Google Sheets, atau aplikasi spreadsheet apa pun." } },
      { q: { en: "Is it private?", id: "Apakah privat?" }, a: { en: "Yes. The export runs in your browser.", id: "Ya. Ekspor berjalan di browser Anda." } },
    ],
    [{ label: { en: "SHP to GeoJSON", id: "SHP ke GeoJSON" }, href: "/convert/shp-to-geojson" }, { label: { en: "Measurements", id: "Pengukuran" }, href: "/tools/calculate" }],
  ),
  "shp-to-gpx": conv(
    "shp-to-gpx", "shp", "gpx",
    "Convert SHP to GPX Online (Free) | Shp.Aid",
    "Convert shapefile to GPX online for GPS devices in your browser. No upload. Konversi SHP ke GPX online gratis.",
    { en: "Convert SHP to GPX Online", id: "Convert SHP ke GPX Online" },
    { en: "Convert a shapefile to GPX for GPS devices and outdoor apps directly in your browser, reprojected to WGS84.", id: "Convert shapefile ke GPX untuk perangkat GPS dan aplikasi outdoor langsung di browser, diproyeksikan ke WGS84." },
    "shp to gpx",
    ["shp to gpx", "shapefile to gpx", "convert shapefile to gpx"],
    [
      { q: { en: "What is GPX used for?", id: "GPX dipakai untuk apa?" }, a: { en: "GPX is the standard format for GPS tracks, routes, and waypoints.", id: "GPX adalah format standar untuk track, rute, dan waypoint GPS." } },
      { q: { en: "Is it reprojected?", id: "Apakah diproyeksikan ulang?" }, a: { en: "Yes, to WGS84 so it works on GPS devices and apps.", id: "Ya, ke WGS84 agar bekerja di perangkat dan aplikasi GPS." } },
      { q: { en: "Is my file uploaded?", id: "Apakah file saya diunggah?" }, a: { en: "No. Conversion runs in your browser.", id: "Tidak. Konversi berjalan di browser Anda." } },
    ],
    [{ label: { en: "SHP to KML", id: "SHP ke KML" }, href: "/convert/shp-to-kml" }, { label: { en: "Coordinate converter", id: "Konverter koordinat" }, href: "/tools/coordinate" }],
  ),
  "shp-to-dxf": conv(
    "shp-to-dxf", "shp", "dxf",
    "Convert SHP to DXF Online (Free) | Shp.Aid",
    "Convert shapefile to DXF online for CAD in your browser. No upload. Konversi SHP ke DXF online gratis.",
    { en: "Convert SHP to DXF Online", id: "Convert SHP ke DXF Online" },
    { en: "Convert a shapefile to DXF for CAD software directly in your browser, without uploading your data.", id: "Convert shapefile ke DXF untuk software CAD langsung di browser, tanpa mengunggah data Anda." },
    "shp to dxf",
    ["shp to dxf", "shapefile to dxf", "convert shapefile to cad"],
    [
      { q: { en: "What is DXF used for?", id: "DXF dipakai untuk apa?" }, a: { en: "DXF is a CAD interchange format read by AutoCAD and most CAD tools.", id: "DXF adalah format pertukaran CAD yang dibaca AutoCAD dan kebanyakan tool CAD." } },
      { q: { en: "Which units are used?", id: "Satuan apa yang dipakai?" }, a: { en: "The source coordinate system's units are kept in the DXF.", id: "Satuan sistem koordinat sumber dipertahankan dalam DXF." } },
      { q: { en: "Is it private?", id: "Apakah privat?" }, a: { en: "Yes. Conversion happens in your browser.", id: "Ya. Konversi terjadi di browser Anda." } },
    ],
    [{ label: { en: "SHP to GeoJSON", id: "SHP ke GeoJSON" }, href: "/convert/shp-to-geojson" }, { label: { en: "Reproject", id: "Reproyeksi" }, href: "/tools/reproject" }],
  ),
};

export const CONVERT_HUB = {
  slug: "/convert",
  title: "Shapefile Converter Online (SHP, GeoJSON, KML, CSV) | Shp.Aid",
  description: DEFAULT_DESC,
  h1: { en: "Shapefile Converter Online", id: "Konverter Shapefile Online" } as L,
  intro: {
    en: "Convert shapefiles to and from GeoJSON, KML, CSV, GPX, and DXF in your browser. Pick a conversion below, or use the converter to choose any From and To formats.",
    id: "Konversi shapefile ke dan dari GeoJSON, KML, CSV, GPX, dan DXF di browser. Pilih konversi di bawah, atau pakai konverter untuk memilih format asal dan tujuan apa pun.",
  } as L,
  related: Object.values(CONVERT_PAGES).map((c) => ({ label: c.h1, href: c.slug })),
};

export function getConvertSeo(pair: string | null | undefined): ConvertSeo | null {
  return pair ? CONVERT_PAGES[pair] ?? null : null;
}

export function localizeConvert(seo: ConvertSeo, loc: Locale): LocalizedSeo {
  return {
    h1: pick(seo.h1, loc),
    intro: pick(seo.intro, loc),
    howTo: {
      name: loc === "id" ? `Cara convert ${IN(seo.input)} ke ${IN(seo.output)}` : `How to convert ${IN(seo.input)} to ${IN(seo.output)}`,
      steps: loc === "id"
        ? [
            { name: "Format", text: `Format dikunci ${IN(seo.input)} -> ${IN(seo.output)} pada halaman ini.` },
            { name: "Unggah", text: `Seret file ${IN(seo.input)} Anda. File dibaca lokal di browser.` },
            { name: "Unduh", text: `Convert dan unduh file ${IN(seo.output)}. Tidak ada data yang keluar dari perangkat.` },
          ]
        : [
            { name: "Pick formats", text: `From is set to ${IN(seo.input)} and To to ${IN(seo.output)} on this page.` },
            { name: "Upload", text: `Drop your ${IN(seo.input)} file. It is read locally in your browser.` },
            { name: "Download", text: `Convert and download the ${IN(seo.output)} file. No data leaves your device.` },
          ],
    },
    features: loc === "id"
      ? ["Tanpa instalasi", "Berbasis browser", "Privat, tanpa unggah", "Gratis, tanpa daftar"]
      : ["No installation", "Browser based", "Private, no upload", "Free, no sign-up"],
    faq: seo.faq.map((f) => ({ q: pick(f.q, loc), a: pick(f.a, loc) })),
    related: seo.related.map((r) => ({ label: pick(r.label, loc), href: r.href })),
  };
}

export function localizeConvertHub(loc: Locale): LocalizedSeo {
  const tool = localizeTool(TOOL_SEO.converter, loc);
  return {
    h1: pick(CONVERT_HUB.h1, loc),
    intro: pick(CONVERT_HUB.intro, loc),
    howTo: tool.howTo,
    features: tool.features,
    faq: tool.faq,
    related: CONVERT_HUB.related.map((r) => ({ label: pick(r.label, loc), href: r.href })),
  };
}

export function buildConvertSchema(c: LocalizedSeo, slug: string) {
  const url = `${BASE}${slug}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        name: c.h1,
        url,
        applicationCategory: "GeographicInformationSystemApplication",
        operatingSystem: "Any (Browser)",
        isAccessibleForFree: true,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        description: c.intro,
      },
      {
        "@type": "HowTo",
        name: c.howTo.name,
        step: c.howTo.steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.name, text: s.text })),
      },
      {
        "@type": "FAQPage",
        mainEntity: c.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Shp.Aid", item: `${BASE}/` },
          { "@type": "ListItem", position: 2, name: "Convert", item: `${BASE}/convert` },
          { "@type": "ListItem", position: 3, name: c.h1, item: url },
        ],
      },
    ],
  };
}
